import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';

/**
 * Дървовиден избор на стокова група — падащ панел, отворен от бутон.
 *
 * Групите досега се избираха от плосък `<select>` с целия път
 * („СТОКИ / Глави / Оригинални глави / Canon Глави - Оригинални") — списък
 * от над 150 реда, в който трудно се разпознава едно ниво от друго. Тук е
 * същото дърво, каквото е в „Наличности": разгъва се стъпка по стъпка,
 * търсенето отваря само пътя до намереното.
 */
export default function GroupPicker({
  onPick, exclude, label = 'добави група…', value, valueLabel, onClear, rootLabel,
}) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open && !groups) api.get('/api/product-groups').then(setGroups).catch(() => setGroups([]));
  }, [open, groups]);

  const parentOf = useMemo(
    () => new Map((groups ?? []).map((g) => [Number(g.id), g.parent_id ? Number(g.parent_id) : null])),
    [groups]
  );

  const tree = useMemo(() => {
    if (!groups) return [];
    const byParent = new Map();
    for (const g of groups) {
      const p = g.parent_id ? Number(g.parent_id) : null;
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(g);
    }
    const build = (parentId) => (byParent.get(parentId) ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'bg'))
      .map((g) => ({ ...g, id: Number(g.id), children: build(Number(g.id)) }));
    return build(null);
  }, [groups]);

  const q = query.trim().toLowerCase();
  const matchedIds = useMemo(() => {
    if (!q || !groups) return null;
    return new Set(groups.filter((g) => g.name.toLowerCase().includes(q)).map((g) => Number(g.id)));
  }, [q, groups]);

  // Пътят до намереното — само тези клони се отварят, останалите стоят затворени.
  const openAncestors = useMemo(() => {
    if (!matchedIds) return null;
    const set = new Set();
    for (const id of matchedIds) {
      let g = parentOf.get(id) ?? null;
      while (g != null && !set.has(g)) { set.add(g); g = parentOf.get(g) ?? null; }
    }
    return set;
  }, [matchedIds, parentOf]);

  function toggle(id) {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function pick(g) {
    if (exclude?.has(g.id)) return;
    onPick(g.id);
    setOpen(false);
    setQuery('');
  }

  function pickRoot() {
    onPick(null);
    setOpen(false);
    setQuery('');
  }

  function close() {
    setOpen(false);
    setQuery('');
  }

  function Node({ g, depth }) {
    if (matchedIds && !matchedIds.has(g.id) && !openAncestors.has(g.id)) return null;
    const isOpen = matchedIds ? (openAncestors.has(g.id) || matchedIds.has(g.id)) : expanded.has(g.id);
    const isExcluded = exclude?.has(g.id);
    const hasChildren = g.children.length > 0;
    /*
     * Едно и също име не може да върши и двете: „разгъни ме" и „избери
     * ме" — при група с деца щракът по името слиза надолу в дървото
     * (както в „Наличности"), не избира направо горното ниво. За да се
     * избере САМАТА група (не подгрупа) стои изричен линк „избери" на
     * реда ѝ — иначе главните групи излизаха единствените избираеми,
     * защото първият клик по тях затваряше панела, преди изобщо да се
     * стигне до подгрупите им.
     */
    const nameAction = hasChildren ? () => toggle(g.id) : () => pick(g);
    return (
      <div>
        <div className="tree-row" style={{ paddingLeft: depth * 16 }}>
          {hasChildren ? (
            <span className="tree-toggle" role="button"
              onMouseDown={(e) => { e.preventDefault(); toggle(g.id); }}>
              {isOpen ? '▾' : '▸'}
            </span>
          ) : <span className="tree-toggle" />}
          <span
            className="tree-name"
            role="button"
            style={{ cursor: isExcluded && !hasChildren ? 'default' : 'pointer' }}
            title={hasChildren ? (isOpen ? 'свий' : 'разгъни') : (isExcluded ? 'вече е добавена' : 'избери тази група')}
            /*
             * onMouseDown + preventDefault, не onClick — докато полето за
             * търсене е фокусирано (autoFocus по-долу) и клавиатурата е
             * отворена, тап на телефон размества фокуса преди „click" да
             * пристигне и изборът се губи. Виж същия бъг в Lookup.jsx.
             */
            onMouseDown={(e) => { e.preventDefault(); if (!isExcluded || hasChildren) nameAction(); }}
          >
            {g.name}{isExcluded && !hasChildren ? ' · добавена' : ''}
          </span>
          {!isExcluded && (
            <button
              type="button" className="link" style={{ marginLeft: 'auto', fontSize: 11, flex: 'none' }}
              onMouseDown={(e) => { e.preventDefault(); pick(g); }}
            >
              избери
            </button>
          )}
        </div>
        {isOpen && g.children.map((c) => <Node key={c.id} g={c} depth={depth + 1} />)}
      </div>
    );
  }

  if (!open) {
    if (value) {
      return (
        <span className="picked">
          {valueLabel ?? value}
          <button className="link" onClick={() => onClear?.()} title="махни">×</button>
        </span>
      );
    }
    return <button type="button" onClick={() => setOpen(true)}>{label}</button>;
  }

  return (
    <div className="lookup">
      <div
        className="lookup-list"
        style={{ minWidth: 340, maxHeight: 360, padding: '8px 4px', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ display: 'flex', gap: 6, padding: '0 6px 6px' }}>
          <input
            type="search" autoFocus autoComplete="off" placeholder="търсене на група"
            value={query} onChange={(e) => setQuery(e.target.value)}
            style={{ flex: 1 }}
          />
          <button type="button" onClick={close}>затвори</button>
        </div>
        <div style={{ overflowY: 'auto' }}>
          {rootLabel && !q && (
            <div className="tree-row" style={{ fontStyle: 'italic' }}>
              <span className="tree-toggle" />
              <span className="tree-name" role="button" style={{ cursor: 'pointer' }}
                onMouseDown={(e) => { e.preventDefault(); pickRoot(); }}>
                {rootLabel}
              </span>
            </div>
          )}
          {groups === null
            ? <div className="muted" style={{ padding: '4px 6px' }}>зареждане…</div>
            : tree.map((g) => <Node key={g.id} g={g} depth={0} />)}
        </div>
      </div>
    </div>
  );
}
